import React, {useEffect, useState, useRef} from 'react';
import { IconButton, Button, Box, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Breadcrumbs, LinearProgress, Typography, Icon, Stack, Tooltip } from '@mui/material';
import { Folder, InsertDriveFile, Delete, Download, Upload, Check, Clear, CreateNewFolder, KeyboardReturn, ArrowBack, Refresh, Close, Edit } from '@mui/icons-material';
import ConfirmationDialog from './ConfirmationDialog';
import Logs from './Logs';

const DEFAULT_NEW_FOLDER_NAME = 'New Folder';

// --- FTP recursive-delete helpers ---

function _ftpListDir(mavId, ftpPath) {
    return new Promise((resolve) => {
        window.api.onceMavFtpListResponse((_ev, _id, _path, result) => resolve(result || []));
        window.api.mavFtpList(mavId, ftpPath);
    });
}

function _ftpRmFile(mavId, ftpPath) {
    return new Promise((resolve) => {
        window.api.onceMavFtpRmResponse((_ev, _id, _path, result) => resolve(result));
        window.api.mavFtpRm(mavId, ftpPath);
    });
}

function _ftpRmdirEmpty(mavId, ftpPath) {
    return new Promise((resolve) => {
        window.api.onceMavFtpRmdirResponse((_ev, _id, _path, result) => resolve(result));
        window.api.mavFtpRmdir(mavId, ftpPath);
    });
}

async function _ftpDeleteRecursively(mavId, ftpPath) {
    const items = await _ftpListDir(mavId, ftpPath);
    if (!items) return false; // list failed (NAK from vehicle)
    for (const item of items) {
        const childPath = ftpPath === '/' ? `/${item.name}` : `${ftpPath}/${item.name}`;
        if (item.type === 'F') {
            const ok = await _ftpRmFile(mavId, childPath);
            if (!ok) return false;
        } else if (item.type === 'D') {
            const ok = await _ftpDeleteRecursively(mavId, childPath);
            if (!ok) return false;
        }
    }
    return _ftpRmdirEmpty(mavId, ftpPath);
}

const SectionHeader = ({ title, action }) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'action.hover' }}
    >
        {typeof title === 'string'
            ? <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
            : title}
        {action}
    </Stack>
);

const Section = ({ title, action, children, sx, bodySx }) => (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...sx }}>
        <SectionHeader title={title} action={action} />
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...bodySx }}>
            {children}
        </Box>
    </Paper>
);

export default function MavlinkFtpEditor({ selectedMavId }) {
    const [fileList, setFileList] = useState({});
    const [ftpListResponseTimestamp, setFtpListResponseTimestamp] = useState(0);
    const [selectedPath, setSelectedPath] = useState('/');
    const [isDownloading, setIsDownloading] = useState(false);
    const [processingFileItem, setProcessingFileItem] = useState(null);
    const [downloadingFtpPath, setDownloadingFtpPath] = useState(null);
    const [downloadedSize, setDownloadedSize] = useState(0);
    const [downloadingSpeed, setDownloadingSpeed] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadingFileName, setUploadingFileName] = useState(null);
    const [uploadingTotalSize, setUploadingTotalSize] = useState(null);
    const [uploadedSize, setUploadedSize] = useState(0);
    const [uploadedPercentage, setUploadedPercentage] = useState(0);
    const [uploadingSpeed, setUploadingSpeed] = useState(0);
    const [renameItem, setRenameItem] = useState(null);
    const [newName, setNewName] = useState('');
    const [deleteItem, setDeleteItem] = useState(null);
    const [createFolderActive, setCreateFolderActive] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderNameHelperText, setNewFolderNameHelperText] = useState('');

    const [transferHistory, setTransferHistory] = useState([]);
    const downloadInfoRef = useRef(null);
    const uploadInfoRef = useRef(null);

    const [logDownloading, setLogDownloading] = useState(null); // {id, size, received, rate, avgRate, eta}
    const cancelLogDownloadRef = useRef(null);
    const handleLogProgress = (p) => {
        setLogDownloading(p);
        const mavId = selectedMavIdRef.current;
        if (!p) {
            window.dispatchEvent(new CustomEvent('ftp-progress', { detail: { mavId, active: false, percent: 0 } }));
            return;
        }
        const percent = p.size ? Math.min(100, (p.received / p.size) * 100) : null;
        const active = !(p.size && p.received >= p.size);
        window.dispatchEvent(new CustomEvent('ftp-progress', { detail: { mavId, active, percent } }));
    };
    const handleRegisterLogCancel = (fn) => { cancelLogDownloadRef.current = fn; };

    const pushTransferHistory = (record) => {
        setTransferHistory((prev) => [
            { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, timestamp: Date.now(), ...record },
            ...prev,
        ].slice(0, 100));
    };

    const focusedItemRef = useRef(null);
    const selectedMavIdRef = useRef(selectedMavId);
    useEffect(() => { selectedMavIdRef.current = selectedMavId; }, [selectedMavId]);
    const [itemToFocus, setItemToFocus] = useState('');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    useEffect(() => {
        if (focusedItemRef.current) {
            focusedItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [fileList, itemToFocus, ftpListResponseTimestamp]);

    useEffect(() => {
        if (selectedMavId !== null) {
            // window.api.mavFtpList(selectedMavId, '/');
        }
        window.addEventListener('mav-ftp-list-response-update', handleMavFtpListResponseUpdate);
        return () => {
            window.removeEventListener('mav-ftp-list-response-update', handleMavFtpListResponseUpdate);
        };
    }, [selectedMavId, itemToFocus]);

    useEffect(() => {
        window.api.onMavFtpGetProgress(handleMavFtpGetProgress);
        window.api.onMavFtpPutProgress(handleMavFtpPutProgress);
        window.api.onMavFtpTerminateSession(handleMavFtpTerminateSession);
        return () => {
            window.api.removeOnMavFtpGetProgress(handleMavFtpGetProgress);
            window.api.removeOnMavFtpPutProgress(handleMavFtpPutProgress);
            window.api.removeOnMavFtpTerminateSession(handleMavFtpTerminateSession);
        };
    }, []);


    const getAbsFtpPath = (dirItemName) => {
        if (selectedPath !== '/') {
            return selectedPath + '/' + dirItemName;
        } else {
            return '/' + dirItemName;
        };
    };

    const sendDelete = async () => {
        const dirItem = deleteItem;
        if (!dirItem) {
            setOpenDeleteDialog(false);
            return;
        }
        const absFtpPath = getAbsFtpPath(dirItem.name);
        setOpenDeleteDialog(false);
        if (dirItem.type === 'D') {
            const ok = await _ftpDeleteRecursively(selectedMavId, absFtpPath);
            if (ok) {
                const currentDirItems = window.mavFtpResponse[selectedMavId]?.[selectedPath];
                if (currentDirItems) {
                    window.mavFtpResponse[selectedMavId][selectedPath] = currentDirItems.filter(item => item.name !== dirItem.name);
                    setFtpListResponseTimestamp(Date.now());
                }
            } else {
                alert("Failed to delete folder");
            }
        } else if (dirItem.type === 'F') {
            window.api.mavFtpRm(selectedMavId, absFtpPath);
            window.api.onceMavFtpRmResponse(handleMavFtpRmResponse);
        }
    };

    const sendRename = (dirItem) => {
        if (!dirItem || newName === '') {
            return;
        }
        const originPath = getAbsFtpPath(dirItem.name)
        const newPath = getAbsFtpPath(newName);
        window.api.mavFtpRename(selectedMavId, originPath, newPath);
        window.api.onceMavFtpRenameResponse(handleMavFtpRenameResponse);
    };

    const sendUpload = () => {
        window.api.mavFtpPut(selectedMavId, selectedPath);
        window.api.onceMavFtpPutResponse(handleMavFtpPutResponse);
    };

    const sendMkdir = () => {
        if (newFolderName === '') {
            setNewFolderNameHelperText('Folder name is required');
            return;
        };

        if (!fileList[selectedPath]) {
            setNewFolderNameHelperText('Refresh before creating a folder');
            return;
        }

        const folderExists = fileList[selectedPath].some(item => item.name === newFolderName && item.type === 'D');
        if (folderExists) {
            setNewFolderNameHelperText('Folder name already exists');
            return;
        }

        setItemToFocus(newFolderName);
        window.api.mavFtpMkdir(selectedMavId, getAbsFtpPath(newFolderName)); 
        window.api.onceMavFtpMkdirResponse(handleMavFtpMkdirResponse)
    };

    const sendDownload = (dirItem) => {
        if (dirItem.type !== 'F') {
            console.error("Not a file");
            return;
        }
        const absFtpPath = getAbsFtpPath(dirItem.name);
        setProcessingFileItem(dirItem);
        // Show the progress modal immediately on click instead of waiting
        // for the first progress packet to arrive.
        setDownloadingFtpPath(absFtpPath);
        setDownloadedSize(0);
        setDownloadingSpeed(0);
        setIsDownloading(true);
        downloadInfoRef.current = {
            kind: 'download',
            name: dirItem.name,
            path: absFtpPath,
            size: dirItem.size,
            completed: false,
        };
        window.api.mavFtpget(selectedMavId, absFtpPath);
    };

    const sendTerminateSession = () => {
        window.api.mavFtpTerminateSession(selectedMavId);
    };

    const handleBreadcrumbClick = (path) => {
        setSelectedPath(path);
    };

    const handleEnter = (dirItem) => {
        let dirPath = "/";
        if (dirItem) {
            if (selectedPath === '/') {
                dirPath = "/" + dirItem.name;
            } else {
                dirPath = selectedPath + "/" + dirItem.name;
            }
        }
        window.api.mavFtpList(selectedMavId, dirPath);
        setSelectedPath(dirPath);
    };
    
    const handleMavFtpPutResponse = (event, srcSystem, filePath, fileSize, result) => {
        if (srcSystem === selectedMavId && result) {
            const fileName = filePath.split('/').pop();
            setItemToFocus(fileName);
            window.mavFtpResponse[selectedMavId][selectedPath].push({name: fileName, type: 'F', size: fileSize});
            setFtpListResponseTimestamp(Date.now());
            setIsUploading(false);
            setUploadedPercentage(100);
            if (uploadInfoRef.current && !uploadInfoRef.current.completed) {
                uploadInfoRef.current.completed = true;
                pushTransferHistory({
                    kind: 'upload',
                    name: fileName,
                    path: filePath,
                    size: fileSize,
                    status: 'completed',
                });
            }
            uploadInfoRef.current = null;
        } else {
            if (uploadInfoRef.current && !uploadInfoRef.current.completed) {
                pushTransferHistory({
                    kind: 'upload',
                    name: uploadInfoRef.current.name,
                    path: uploadInfoRef.current.path,
                    size: uploadInfoRef.current.size,
                    status: 'failed',
                });
            }
            uploadInfoRef.current = null;
            alert("Failed to upload");
        }
    };

    const handleMavFtpMkdirResponse = (event, srcSystem, ftpPath, result) => {
        if (srcSystem === selectedMavId && result) {
            // window.api.mavFtpList(selectedMavId, selectedPath);
            const folderName = ftpPath.split('/').pop();
            setItemToFocus(folderName);
            window.mavFtpResponse[selectedMavId][selectedPath].push({name: folderName, type: 'D'});
            setFtpListResponseTimestamp(Date.now());
            setCreateFolderActive(false);
            setNewFolderName('');
            setNewFolderNameHelperText('');
        } else {
            alert("Failed to create folder");
        }
    };

    const handleMavFtpListResponseUpdate = (event) => {
        setFtpListResponseTimestamp(Date.now());
        if (selectedMavId !== null) {
            if (window.mavFtpResponse[selectedMavId]) {
                setFileList(window.mavFtpResponse[selectedMavId]);
            }
        }
    }

    const handleMavFtpTerminateSession = (event, srcSystem) => {
        if (srcSystem === selectedMavIdRef.current) {
            if (downloadInfoRef.current && !downloadInfoRef.current.completed) {
                pushTransferHistory({
                    kind: 'download',
                    name: downloadInfoRef.current.name,
                    path: downloadInfoRef.current.path,
                    size: downloadInfoRef.current.size,
                    status: 'cancelled',
                });
            }
            if (uploadInfoRef.current && !uploadInfoRef.current.completed) {
                pushTransferHistory({
                    kind: 'upload',
                    name: uploadInfoRef.current.name,
                    path: uploadInfoRef.current.path,
                    size: uploadInfoRef.current.size,
                    status: 'cancelled',
                });
            }
            downloadInfoRef.current = null;
            uploadInfoRef.current = null;
            setIsDownloading(false);
            setIsUploading(false);
            // Auto-dismiss the progress dialog — the file has already
            // been saved (browser_fs flushes on close), so leaving the
            // "Downloading ..." row up just confuses the user.
            setDownloadingFtpPath(null);
            setUploadingFileName(null);
            setUploadingTotalSize(null);
            setUploadedSize(0);
            setProcessingFileItem(null);
            window.dispatchEvent(new CustomEvent('ftp-progress', { detail: { mavId: srcSystem, active: false, percent: 0 } }));
        }
    };

    const handleMavFtpRmResponse = (event, srcSystem, ftpPath, result) => {
        if (srcSystem === selectedMavId) {
            if (result) {
                const currentDirItems = window.mavFtpResponse[selectedMavId][selectedPath];
                const fileName = ftpPath.split('/').pop();
                window.mavFtpResponse[selectedMavId][selectedPath] = currentDirItems.filter(item => item.name !== fileName);
                setFtpListResponseTimestamp(Date.now());
            } else {
                alert("Failed to delete file");
            }
        }
    };

    const handleMavFtpRmdirResponse = (event, srcSystem, ftpPath, result) => {
        if (srcSystem === selectedMavId) {
            if (result) {
                const currentDirItems = window.mavFtpResponse[selectedMavId][selectedPath];
                const dirName = ftpPath.split('/').pop();
                window.mavFtpResponse[selectedMavId][selectedPath] = currentDirItems.filter(item => item.name !== dirName);
                setFtpListResponseTimestamp(Date.now());
            }
        }
    };

    const handleMavFtpRenameResponse = (event, srcSystem, originPath, newPath, result) => {
        if (srcSystem === selectedMavId) {
            if (result) {
                const currentDirItems = window.mavFtpResponse[selectedMavId][selectedPath];
                const originName = originPath.split('/').pop();
                const newName = newPath.split('/').pop();
                window.mavFtpResponse[selectedMavId][selectedPath] = currentDirItems.map(item => {
                    if (item.name === originName) {
                        item.name = newName;
                    }
                    return item;
                });
                setFtpListResponseTimestamp(Date.now());
            } else {
                console.alert("Failed to rename");
            }
        }
        closeRenameField();
    };

    const handleMavFtpGetProgress = (event, srcSystem, ftpPath, totalReadSize, rate) => {
        // console.log("handleMavFtpGetProgress", srcSystem, ftpPath, totalReadSize, rate);
        if (srcSystem === selectedMavIdRef.current) {
            setDownloadingFtpPath(ftpPath);
            setDownloadedSize(totalReadSize);
            if (typeof rate === 'number') setDownloadingSpeed(rate);
            setIsDownloading(true);
            const info = downloadInfoRef.current;
            if (info && !info.completed && info.size && totalReadSize >= info.size) {
                info.completed = true;
                pushTransferHistory({
                    kind: 'download',
                    name: info.name,
                    path: info.path,
                    size: info.size,
                    status: 'completed',
                });
            }
            const totalSize = info && info.size ? info.size : null;
            const percent = totalSize ? Math.min(100, (totalReadSize / totalSize) * 100) : null;
            window.dispatchEvent(new CustomEvent('ftp-progress', { detail: { mavId: srcSystem, active: true, percent } }));
        }
    };

    const handleMavFtpPutProgress = (event, srcSystem, filePath, writeAcks, wirteTotal, rate, fileSize) => {
        if (srcSystem === selectedMavIdRef.current) {
            setIsUploading(true);
            const percentage = wirteTotal > 0 ? (writeAcks / wirteTotal * 100) : 0;
            setUploadedPercentage(percentage);
            const fileName = filePath.split('/').pop();
            setUploadingFileName(fileName);
            setUploadingSpeed(rate);
            const totalBytes = typeof fileSize === 'number' ? fileSize : null;
            if (totalBytes != null) {
                setUploadingTotalSize(totalBytes);
                setUploadedSize(Math.min(totalBytes, Math.round(totalBytes * percentage / 100)));
            }
            if (!uploadInfoRef.current || uploadInfoRef.current.path !== filePath) {
                uploadInfoRef.current = {
                    kind: 'upload',
                    name: fileName,
                    path: filePath,
                    size: totalBytes,
                    completed: false,
                };
            } else if (totalBytes != null) {
                uploadInfoRef.current.size = totalBytes;
            }
            window.dispatchEvent(new CustomEvent('ftp-progress', { detail: { mavId: srcSystem, active: true, percent: percentage } }));
        }
    };

    const openRenameField = (dirItem) => {
        setRenameItem(dirItem);
        setNewName(dirItem.name);
    };

    const closeRenameField = () => {
        setRenameItem(null);
        setNewName('');
    };

    const handleGoBack = () => {
        const pathSegments = selectedPath.split('/').filter(Boolean);
        const newPath = pathSegments.slice(0, -1).join('/');
        setSelectedPath(newPath ? `/${newPath}/` : '/');
        window.api.mavFtpList(selectedMavId, newPath ? `/${newPath}/` : '/');
    };

    const handleRefresh = () => {
        window.api.mavFtpList(selectedMavId, selectedPath);
    };

    const _renderRenameEditButton = (dirItem) => {
        if (renameItem && dirItem.name === renameItem.name) {
            return null;
        }
        return (
            <IconButton
                onClick={() => openRenameField(dirItem)}
                disabled={isDownloading || isUploading}
            >
                <Edit />
            </IconButton>
        )
    };

    const _renderRenameField = (dirItem) => {
        if (!renameItem || dirItem.name !== renameItem.name) {
            return null;
        }
        return (
            <Box sx={{display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center'}}>
                <TextField
                    label="New Name"
                    variant="outlined"
                    size="small"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                />
                <Box>
                    <IconButton
                        onClick={() => sendRename(dirItem)}
                        disabled={isDownloading || isUploading}
                        color="success"
                    >
                        <Check />
                    </IconButton>
                </Box>
                <Box>
                    <IconButton
                        onClick={() => closeRenameField()}
                        disabled={isDownloading || isUploading}
                        color="error"
                    >
                        <Clear />
                    </IconButton>
                </Box>
            </Box>
        )
    };

    const _renderDownloadingProgress = () => {
        if (!processingFileItem) {
            return null;
        }
        const dirItem = processingFileItem;
        const absFtpPath = getAbsFtpPath(dirItem.name);
        if (processingFileItem.type === 'F' && absFtpPath === downloadingFtpPath) {
            const progress = downloadedSize / processingFileItem.size * 100;
            const sizeInKB = processingFileItem.size / 1024;
            const downloadedInKB = downloadedSize / 1024;
            const sizeInMB = sizeInKB / 1024;
            const downloadedInMB = downloadedInKB / 1024;

            const sizeDisplay = sizeInKB > 1024
                ? `${sizeInMB.toFixed(2)} MB`
                : `${sizeInKB.toFixed(2)} KB`;

            const downloadedDisplay = downloadedInKB > 1024
                ? `${downloadedInMB.toFixed(2)} MB`
                : `${downloadedInKB.toFixed(2)} KB`;

            let endHelperText = "";
            if (downloadedSize === processingFileItem.size) {
                endHelperText = "Complete!";
            }
            const speedDisplay = downloadingSpeed > 1024
                ? `${(downloadingSpeed / 1024).toFixed(2)} MB/s`
                : `${downloadingSpeed.toFixed(2)} KB/s`;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Download fontSize="small" color="primary" />
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="caption">Downloading</Typography>
                            <Box
                                component="code"
                                sx={{
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                    fontSize: '0.75rem',
                                    px: 0.6,
                                    py: 0.1,
                                    borderRadius: 0.5,
                                    bgcolor: 'rgba(127,127,127,0.18)',
                                    border: '1px solid rgba(127,127,127,0.3)',
                                    lineHeight: 1.4,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >{downloadingFtpPath}</Box>
                            <Typography variant="caption" sx={{ flexShrink: 0 }}>{`( ${downloadedDisplay} / ${sizeDisplay} ) @ ${speedDisplay} ${endHelperText}`}</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
                    </Box>
                    {!isDownloading ? (
                        <IconButton
                            onClick={() => (setProcessingFileItem(null))}
                            disabled={isDownloading}
                        >
                            <Close />
                        </IconButton>
                    ) : null}
                </Box>
            );
        } else {
            return null;
        }
    };

    const _renderUploadingProgress = () => {
        if (!uploadingFileName) {
            return null;
        }

        const uploadingSpeedText = uploadingSpeed > 1024 ? `${(uploadingSpeed / 1024).toFixed(2)} MB/s` : `${uploadingSpeed.toFixed(2)} KB/s`;
        const sizeText = uploadingTotalSize != null
            ? `( ${_formatBytes(uploadedSize)} / ${_formatBytes(uploadingTotalSize)} ) ${uploadedPercentage.toFixed(0)}% @ ${uploadingSpeedText}`
            : `(${uploadedPercentage.toFixed(0)}%) - ${uploadingSpeedText}`;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Upload fontSize="small" color="primary" />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                        <Typography variant="caption">Uploading</Typography>
                        <Box
                            component="code"
                            sx={{
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                fontSize: '0.75rem',
                                px: 0.6,
                                py: 0.1,
                                borderRadius: 0.5,
                                bgcolor: 'rgba(127,127,127,0.18)',
                                border: '1px solid rgba(127,127,127,0.3)',
                                lineHeight: 1.4,
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >{uploadingFileName}</Box>
                        <Typography variant="caption" sx={{ flexShrink: 0 }}>{sizeText}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={uploadedPercentage} sx={{ mt: 0.5 }} />
                </Box>
                {!isUploading || uploadedPercentage === 100 ? (
                    <IconButton
                        onClick={() => (setUploadingFileName(null))}
                        disabled={isUploading}
                    >
                        <Close />
                    </IconButton>
                ) : null}
            </Box>
        );
    };

    const _formatBytes = (bytes) => {
        if (bytes == null || isNaN(bytes)) return '';
        if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${bytes} B`;
    };

    const _statusColor = (status) => {
        if (status === 'completed') return 'success.main';
        if (status === 'cancelled') return 'warning.main';
        return 'error.main';
    };

    const _renderTransferHistory = () => {
        if (transferHistory.length === 0) return null;
        return transferHistory.map((item) => (
            <Box
                key={item.id}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.5,
                    borderBottom: '1px dashed',
                    borderColor: 'divider',
                }}
            >
                {item.kind === 'download'
                    ? <Download fontSize="small" sx={{ color: 'text.secondary' }} />
                    : <Upload fontSize="small" sx={{ color: 'text.secondary' }} />}
                <Box
                    component="code"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={item.path || item.name}
                >
                    {item.path || item.name}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                    {_formatBytes(item.size)}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ color: _statusColor(item.status), flexShrink: 0, textTransform: 'capitalize' }}
                >
                    {item.status}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                    {new Date(item.timestamp).toLocaleTimeString()}
                </Typography>
            </Box>
        ));
    };

    const _renderLogDownloadingProgress = () => {
        if (!logDownloading) return null;
        const { id, size, received, rate } = logDownloading;
        const percent = size ? Math.min(100, (received / size) * 100) : 0;
        const fmt = (n) => {
            if (n == null) return '—';
            if (n < 1024) return `${n} B`;
            if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
            if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
            return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
        };
        const rateText = rate > 1024 * 1024
            ? `${(rate / 1024 / 1024).toFixed(2)} MB/s`
            : rate > 1024
                ? `${(rate / 1024).toFixed(1)} KB/s`
                : `${(rate || 0).toFixed(0)} B/s`;
        const endHelperText = size && received >= size ? 'Complete!' : '';
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Download fontSize="small" color="primary" />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                        <Typography variant="caption">Downloading</Typography>
                        <Box
                            component="code"
                            sx={{
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                fontSize: '0.75rem',
                                px: 0.6,
                                py: 0.1,
                                borderRadius: 0.5,
                                bgcolor: 'rgba(127,127,127,0.18)',
                                border: '1px solid rgba(127,127,127,0.3)',
                                lineHeight: 1.4,
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >{`log_${id}.bin`}</Box>
                        <Typography variant="caption" sx={{ flexShrink: 0 }}>
                            {`( ${fmt(received)} / ${fmt(size)} ) @ ${rateText} ${endHelperText}`}
                        </Typography>
                    </Box>
                    <LinearProgress variant={size ? 'determinate' : 'indeterminate'} value={percent} sx={{ mt: 0.5 }} />
                </Box>
            </Box>
        );
    };

    const _renderTransferPanelBody = () => {
        const hasActive = Boolean(processingFileItem) || Boolean(uploadingFileName) || Boolean(logDownloading);
        const hasHistory = transferHistory.length > 0;
        return (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {_renderDownloadingProgress()}
                {_renderUploadingProgress()}
                {_renderLogDownloadingProgress()}
                {_renderTransferHistory()}
                {!hasActive && !hasHistory && (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="caption">No transfers yet</Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const _renderTransferPanelAction = () => {
        const hasHistory = transferHistory.length > 0;
        const ftpActive = isDownloading || isUploading;
        const logActive = Boolean(logDownloading);
        return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
                {(ftpActive || logActive) && (
                    <Button
                        onClick={() => {
                            if (logActive && cancelLogDownloadRef.current) {
                                cancelLogDownloadRef.current();
                            }
                            if (ftpActive) sendTerminateSession();
                        }}
                        color="error"
                        size="small"
                        variant="contained"
                    >
                        Stop
                    </Button>
                )}
                {hasHistory && (
                    <Tooltip title="Clear history">
                        <IconButton onClick={() => setTransferHistory([])}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        );
    };

    const _renderCreateFolderField = () => {
        if (createFolderActive) {
            return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                        placeholder="Folder name"
                        error={newFolderNameHelperText !== ''}
                        helperText={newFolderNameHelperText || undefined}
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        sx={{ width: 180 }}
                    />
                    <Tooltip title="Create">
                        <span>
                            <IconButton
                                onClick={() => sendMkdir()}
                                disabled={isDownloading || isUploading}
                                color="success"
                            >
                                <Check fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                        <span>
                            <IconButton
                                onClick={() => setCreateFolderActive(false)}
                                disabled={isDownloading || isUploading}
                                color="error"
                            >
                                <Clear fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            );
        }
        return (
            <Tooltip title="New folder">
                <span>
                    <IconButton
                        onClick={() => setCreateFolderActive(true)}
                        disabled={isDownloading || isUploading || !fileList[selectedPath]}
                    >
                        <CreateNewFolder fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    const _renderBreadcrumbs = () => {
        const pathSegments = selectedPath.split('/').filter(Boolean);
        const paths = pathSegments.map((segment, index) => {
            const path = '/' + pathSegments.slice(0, index + 1).join('/');
            return { name: segment, path };
        });

        return (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <Tooltip title="Go up">
                    <span>
                        <IconButton
                            onClick={handleGoBack}
                            disabled={selectedPath === '/' || isDownloading || isUploading}
                        >
                            <ArrowBack fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="/"
                    sx={{
                        minWidth: 0,
                        '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
                        '& .MuiBreadcrumbs-li': { minWidth: 0 },
                    }}
                >
                    <Button
                        color="primary"
                        size="small"
                        disabled={isDownloading || isUploading}
                        onClick={() => handleBreadcrumbClick('/')}
                    >
                        Root
                    </Button>
                    {paths.map((segment, index) => (
                        <Button
                            key={index}
                            color="primary"
                            size="small"
                            disabled={isDownloading || isUploading}
                            onClick={() => handleBreadcrumbClick(segment.path)}
                            sx={{ minWidth: 0, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {segment.name}
                        </Button>
                    ))}
                </Breadcrumbs>
            </Stack>
        );
    };

    const _renderFileList = () => {
        const rootPath = fileList['/'];
        if (!rootPath) {
            return (
                <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                        Click Refresh to load the root directory
                    </TableCell>
                </TableRow>
            );
        }

        const displayPath = fileList[selectedPath];
        if (!displayPath) {
            return (
                <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                        Loading…
                    </TableCell>
                </TableRow>
            );
        }

        if (displayPath.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                        No files found.
                    </TableCell>
                </TableRow>
            );
        }

        const _renderPathDetail = (dirItem) => {
            if (dirItem.type === 'D') {
                return (
                    <Button
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', minWidth: 0 }}
                        disabled={isDownloading || isUploading}
                        onClick={() => handleEnter(dirItem)}
                        startIcon={<Folder fontSize="small" sx={{ color: 'primary.main' }} />}
                    >
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {dirItem.name}
                        </Typography>
                    </Button>
                );
            }
            return (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
                    <InsertDriveFile fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {dirItem.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        {dirItem.size > 1024 * 1024
                            ? `${(dirItem.size / (1024 * 1024)).toFixed(2)} MB`
                            : `${(dirItem.size / 1024).toFixed(2)} KB`}
                    </Typography>
                </Stack>
            );
        };

        return displayPath
            .filter(dirItem => dirItem.name !== '.' && dirItem.name !== '..')
            .map((dirItem, index) => (
                <TableRow
                    key={index}
                    hover
                    ref={dirItem.name === itemToFocus ? focusedItemRef : null}
                >
                    <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {_renderPathDetail(dirItem)}
                            {_renderRenameEditButton(dirItem)}
                            {_renderRenameField(dirItem)}
                        </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                        <Stack direction="row" justifyContent="flex-end" spacing={0.25}>
                            {dirItem.type === 'D' ? (
                                <Tooltip title="Open">
                                    <span>
                                        <IconButton
                                            onClick={() => handleEnter(dirItem)}
                                            disabled={isDownloading || isUploading}
                                        >
                                            <KeyboardReturn fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Download">
                                    <span>
                                        <IconButton
                                            onClick={() => sendDownload(dirItem)}
                                            disabled={isDownloading || isUploading}
                                        >
                                            <Download fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title="Delete">
                                <span>
                                    <IconButton
                                        onClick={() => { setDeleteItem(dirItem); setOpenDeleteDialog(true); }}
                                        disabled={isDownloading || isUploading}
                                    >
                                        <Delete fontSize="small" color="warning" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </TableCell>
                </TableRow>
            ));
    };

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Box sx={{ flex: 1, minHeight: 0 }} style={{ display: 'flex', flexDirection: 'column', margin: 2 }}>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            gap: '2px',
                            p: '2px',
                            boxSizing: 'border-box',
                        }}
                    >
                <Box
                    sx={{
                        flex: 3,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                    }}
                >
                    <Section
                        title={
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ flex: 1, minWidth: 0 }}
                            >
                                {_renderBreadcrumbs()}
                            </Stack>
                        }
                        action={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                {_renderCreateFolderField()}
                                <Tooltip title="Upload file to current folder">
                                    <span>
                                        <IconButton
                                            onClick={sendUpload}
                                            disabled={isDownloading || isUploading || !fileList[selectedPath]}
                                        >
                                            <Upload fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Refresh">
                                    <span>
                                        <IconButton
                                            onClick={handleRefresh}
                                            disabled={isDownloading || isUploading}
                                        >
                                            <Refresh fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        }
                        sx={{ flex: 1, minWidth: 0 }}
                        bodySx={{ p: 0 }}
                    >
                        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                            <Table stickyHeader>
                                <TableBody>{_renderFileList()}</TableBody>
                            </Table>
                        </TableContainer>
                    </Section>

                    <Paper
                        variant="outlined"
                        sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
                    >
                        <Logs
                            selectedMavId={selectedMavId}
                            onLogProgress={handleLogProgress}
                            registerLogCancel={handleRegisterLogCancel}
                            pushTransferHistory={pushTransferHistory}
                        />
                    </Paper>
                </Box>

                <Section
                    title="Transfers"
                    action={_renderTransferPanelAction()}
                    sx={{ flex: 1, minHeight: 140 }}
                    bodySx={{ p: 0 }}
                >
                    {_renderTransferPanelBody()}
                </Section>
                    </Box>
                </Box>
            </div>
            <ConfirmationDialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                onConfirm={sendDelete}
                confirmColor="error"
                title={`Delete ${deleteItem && deleteItem.type === 'D' ? 'Folder:' : 'File:'} ${deleteItem ? getAbsFtpPath(deleteItem.name) : 'No item selected'}`}
                description="Are you sure want to delete?"
            />
        </>
    );
}